import { z } from 'zod';

export const prepItemSchema = z.object({
  itemId: z.string(),
  recipeId: z.string(),
  recipeName: z.string(),
  recipeType: z.enum(['finished', 'sub-recipe', 'component']),
  targetQuantity: z.number(),
  completedQuantity: z.number(),
  unit: z.string(),
  status: z.enum(['pending', 'in-progress', 'completed']),
  assignedTo: z.string().optional(),
  assignedToName: z.string().optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  notes: z.string().optional(),
});

export const prepListSchema = z.object({
  prepListId: z.string(),
  siteId: z.string(),
  venueId: z.string(),
  venueName: z.string(),
  date: z.string(),
  mealPeriod: z.enum(['breakfast', 'lunch', 'dinner', 'all-day']),
  status: z.enum(['draft', 'active', 'completed']),
  items: z.array(prepItemSchema),
  totalItems: z.number(),
  completedItems: z.number(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PrepItem = z.infer<typeof prepItemSchema>;
export type PrepList = z.infer<typeof prepListSchema>;
export type PrepListStatus = PrepList['status'];
export type PrepItemStatus = PrepItem['status'];
export type MealPeriod = PrepList['mealPeriod'];
export type RecipeType = PrepItem['recipeType'];

export const prepListsResponseSchema = z.object({
  data: z.array(prepListSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
  meta: z.object({ siteId: z.string() }),
});

export type PrepListsResponse = z.infer<typeof prepListsResponseSchema>;

export const PREP_LIST_STATUS_LABELS: Record<PrepListStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  completed: 'Completed',
};

export const PREP_LIST_STATUS_COLORS: Record<PrepListStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
};

export const MEAL_PERIOD_LABELS: Record<MealPeriod, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  'all-day': 'All Day',
};

export const RECIPE_TYPE_LABELS: Record<RecipeType, string> = {
  finished: 'Finished',
  'sub-recipe': 'Sub-Recipe',
  component: 'Component',
};

// Batch Planning Types
export const batchItemSchema = z.object({
  itemId: z.string(),
  recipeId: z.string(),
  recipeName: z.string(),
  targetQuantity: z.number(),
  unit: z.string(),
  scheduledTime: z.string().optional(),
  estimatedDuration: z.number().optional(),
  assignedTo: z.string().optional(),
  assignedToName: z.string().optional(),
});

export const batchPlanSchema = z.object({
  batchPlanId: z.string(),
  siteId: z.string(),
  venueId: z.string(),
  venueName: z.string(),
  name: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  status: z.enum(['draft', 'scheduled', 'in-progress', 'completed', 'cancelled']),
  items: z.array(batchItemSchema),
  totalItems: z.number(),
  completedItems: z.number(),
  notes: z.string().optional(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type BatchItem = z.infer<typeof batchItemSchema>;
export type BatchPlan = z.infer<typeof batchPlanSchema>;
export type BatchPlanStatus = BatchPlan['status'];

export const batchPlansResponseSchema = z.object({
  data: z.array(batchPlanSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number(),
  }),
  meta: z.object({ siteId: z.string() }),
});

export type BatchPlansResponse = z.infer<typeof batchPlansResponseSchema>;

export const BATCH_PLAN_STATUS_LABELS: Record<BatchPlanStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const BATCH_PLAN_STATUS_COLORS: Record<BatchPlanStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  'in-progress': 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};
